/** Links identify a chat, never grant access. Authentication remains unchanged. */
export const CHAT_BASE_PATH = "/";
export type ChatLink = { kind: "none" } | { kind: "chat"; id: string } | { kind: "invalid" };
export type ChatHistoryMode = "push" | "replace" | "none";
const validId = (id: string) => /^[a-z0-9][a-z0-9_-]{0,127}$/i.test(id);

export function chatLinkUrl(id: string): string {
  if (!validId(id)) throw new Error("Invalid chat link.");
  return `https://agent.reputifly.cloud${CHAT_BASE_PATH}?chat=${encodeURIComponent(id)}`;
}

export function readChatLink(): ChatLink {
  if (typeof window === "undefined" || !window.location?.href) return { kind: "none" };
  try {
    const url = new URL(window.location.href);
    if (url.pathname !== CHAT_BASE_PATH && url.pathname !== CHAT_BASE_PATH.slice(0, -1)) return { kind: "invalid" };
    const ids = url.searchParams.getAll("chat");
    if (!ids.length) return { kind: "none" };
    return ids.length === 1 && validId(ids[0]!) ? { kind: "chat", id: ids[0]! } : { kind: "invalid" };
  } catch { return { kind: "invalid" }; }
}

export function writeChatLink(id: string | null, mode: ChatHistoryMode = "push"): void {
  if (mode === "none" || typeof window === "undefined" || !window.history || !window.location?.href) return;
  const path = id ? new URL(chatLinkUrl(id)).pathname + "?chat=" + encodeURIComponent(id) : CHAT_BASE_PATH;
  if (window.location.pathname + window.location.search + window.location.hash === path) return;
  // Replace only the location, preserving state owned by the browser/other UI.
  window.history[mode === "replace" ? "replaceState" : "pushState"](window.history.state, "", path);
}

export function hasUnsentChatInput(state: {
  composerDraft: string; attachments: unknown[]; notifyOnDoneArmed: boolean;
}): boolean {
  if (state.composerDraft.trim() || state.attachments.length || state.notifyOnDoneArmed) return true;
  if (typeof document === "undefined" || !document.querySelector) return false;
  // These editors contain staged input not committed to the shared draft yet.
  if (document.querySelector('.clientProposal, .clientProposalLock, .wf-editor, [data-chat-unsaved="true"], .waSheet[aria-label="Choose a date range"]')) return true;
  const textarea = document.querySelector<HTMLTextAreaElement>(".composer2__input");
  return !!textarea?.value.trim();
}

export function chatNavigationBusy(): boolean {
  return typeof document !== "undefined" && !!document.querySelector?.('[data-chat-busy="true"]');
}

export function confirmChatSwitch(state: Parameters<typeof hasUnsentChatInput>[0], closingScheduleOnly = false): boolean {
  if (chatNavigationBusy()) return false;
  const unsent = closingScheduleOnly
    ? typeof document !== "undefined" && !!document.querySelector?.('[data-chat-unsaved="true"]')
    : hasUnsentChatInput(state);
  if (!unsent) return true;
  return typeof window !== "undefined" && typeof window.confirm === "function" &&
    window.confirm("Switch chats? Unsent work may be discarded, including attachments and message options. Cancel to finish or save it first.");
}
